import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { Camera, Eye, Loader2, Trash2, X } from 'lucide-react';

import {
  createSnapshot,
  deleteSnapshot,
  listSnapshots,
} from '../../services/dashboardApi';

const when = (iso) =>
  new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/** The viewer's saved snapshots: save the data as it is now (with the filters
 * in view), open one read-only, delete one. Only the person who saved a
 * snapshot sees it. */
const SnapshotsPanel = ({
  accessToken,
  dashboardId,
  filters,
  canSave,
  onOpen,
  onClose,
}) => {
  const [snapshots, setSnapshots] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(null);

  const load = () =>
    listSnapshots(accessToken, dashboardId)
      .then((res) => setSnapshots(res?.data || []))
      .catch((e) => {
        toast.error(e?.message || String(e));
        setSnapshots([]);
      });

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, dashboardId]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const run = async (key, action) => {
    setBusy(key);
    try {
      await action();
    } catch (e) {
      toast.error(e?.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const save = () =>
    run('save', async () => {
      await createSnapshot(accessToken, dashboardId, name.trim(), filters);
      setName('');
      toast.success('Snapshot saved');
      await load();
    });

  const remove = (id) =>
    run(id, async () => {
      await deleteSnapshot(accessToken, dashboardId, id);
      await load();
    });

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50">
      <aside
        role="dialog"
        aria-label="Dashboard snapshots"
        className="flex h-full w-full max-w-sm flex-col border-l border-zinc-700 bg-zinc-900 text-zinc-200"
      >
        <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-3">
          <h2 className="text-sm font-semibold">Snapshots</h2>
          <button onClick={onClose} title="Close" className="p-1 text-zinc-400">
            <X size={16} />
          </button>
        </div>
        {canSave && (
          <div className="flex flex-col gap-2 border-b border-zinc-800 p-3">
            <p className="text-[11px] leading-snug text-zinc-500">
              Save the numbers as they are now, with the filters in view. A
              snapshot never changes; only you can see your snapshots, and you
              can share one as a public link.
            </p>
            <div className="flex gap-2">
              <input
                aria-label="Snapshot name"
                placeholder="Name (optional), e.g. Sprint 12 end"
                value={name}
                maxLength={80}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && busy === null && save()}
                className="min-w-0 flex-1 rounded-lg border border-zinc-700 bg-zinc-950 px-2 py-1 text-xs focus:border-[#5652CF] focus:outline-none"
              />
              <button
                onClick={save}
                disabled={busy !== null}
                className="flex items-center gap-1.5 rounded-lg bg-[#5652CF] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {busy === 'save' ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Camera size={12} aria-hidden="true" />
                )}
                Save snapshot
              </button>
            </div>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto p-2">
          {!snapshots && <Loader2 className="m-4 animate-spin text-zinc-500" />}
          {snapshots?.length === 0 && (
            <p className="p-2 text-xs text-zinc-500">No snapshots yet.</p>
          )}
          {snapshots?.map((snap) => {
            const filtered = Object.keys(snap.filters || {}).length;
            return (
              <div
                key={snap.id}
                className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-zinc-800/50"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs text-zinc-200">{snap.name}</p>
                  <p className="text-[11px] text-zinc-500">
                    {when(snap.created_at)}
                    {filtered
                      ? ` · ${filtered} filter${filtered > 1 ? 's' : ''}`
                      : ''}
                  </p>
                </div>
                <button
                  onClick={() => onOpen(snap.id)}
                  className="flex items-center gap-1 rounded-md border border-zinc-700 px-2 py-1 text-[11px] hover:bg-zinc-800"
                >
                  <Eye size={12} aria-hidden="true" />
                  Open
                </button>
                {canSave && (
                  <button
                    onClick={() => remove(snap.id)}
                    disabled={busy !== null}
                    title="Delete snapshot"
                    aria-label={`Delete ${snap.name}`}
                    className="rounded-md border border-zinc-700 p-1 text-zinc-400 hover:bg-red-900/40 disabled:opacity-50"
                  >
                    {busy === snap.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} />
                    )}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </aside>
    </div>,
    document.body,
  );
};

export default SnapshotsPanel;
